import { Lock } from 'lucide-react';
import { Input } from '@/ui';
import { MIN_PASSWORD_LENGTH } from './types';
import styles from './AuthForm.module.css';

export interface PasswordPair {
  readonly next: string;
  readonly confirm: string;
}

/**
 * Validates a new-password pair client-side.
 *
 * The length rule mirrors the backend, which returns a 422 below 8 characters; the
 * match rule is ours, since the backend has no concept of a confirmation field.
 * Returns undefined when the field is fine, so it can be passed straight to `error`.
 */
export function validateNewPassword(pair: PasswordPair): {
  next?: string | undefined;
  confirm?: string | undefined;
  ok: boolean;
} {
  const next =
    pair.next.length > 0 && pair.next.length < MIN_PASSWORD_LENGTH
      ? `Use at least ${MIN_PASSWORD_LENGTH} characters.`
      : undefined;

  const confirm =
    pair.confirm.length > 0 && pair.confirm !== pair.next
      ? 'Passwords do not match.'
      : undefined;

  const ok =
    pair.next.length >= MIN_PASSWORD_LENGTH && pair.confirm === pair.next && next === undefined;

  return { next, confirm, ok };
}

export interface NewPasswordFieldsProps {
  readonly value: PasswordPair;
  readonly onChange: (value: PasswordPair) => void;
  readonly disabled?: boolean;
  /** Field-level message from the server, e.g. a 422 on new_password. */
  readonly serverError?: string | undefined;
}

export function NewPasswordFields({
  value,
  onChange,
  disabled = false,
  serverError,
}: NewPasswordFieldsProps) {
  const validation = validateNewPassword(value);

  return (
    <>
      <Input
        label="New password"
        icon={Lock}
        type="password"
        value={value.next}
        onChange={(event) => onChange({ ...value, next: event.target.value })}
        autoComplete="new-password"
        required
        disabled={disabled}
        error={serverError ?? validation.next}
      />

      <Input
        label="Confirm new password"
        icon={Lock}
        type="password"
        value={value.confirm}
        onChange={(event) => onChange({ ...value, confirm: event.target.value })}
        autoComplete="new-password"
        required
        disabled={disabled}
        error={validation.confirm}
      />

      <p className={styles.requirements}>
        Must be at least {MIN_PASSWORD_LENGTH} characters.
      </p>
    </>
  );
}
